import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatDecimals } from 'global/helpers/numberFormatters'
import { localDate } from 'global/helpers/dates'
import { useTranslation } from 'react-i18next'
import { isQuoteOfferAnswer } from 'screens/QuoteDetails/QuoteDetails.service'
import { type QuoteAnswer, QuoteStatus } from 'screens/QuoteDetails/QuoteDetails.types'
import QuoteAnswerAttribute from 'screens/QuoteDetails/QuoteAnswerAttribute'

export interface CarrierQuoteAnswerSummaryProps {
  quoteAnswer: QuoteAnswer
  quoteReference?: string
}
/*
  Quote answer's transit duration is stored in a "P{number}D" format (where D stands for days) required by the TMS contract:
  https://wiki.int.kn/spaces/gscgian/pages/1767086632/FO+ROADLOG-169+Carrier+spot+price+request+per+mail#FO%7CROADLOG169%7CCarrierspotpricerequestpermail-DetailedSpecifications
  This extracts just the days value to display in a form.
*/
export const findDayValue = (input: string): string => {
  const match = new RegExp(/P(\d+(?:\.\d+)?)D/).exec(input) // NOSONAR
  return match?.[1] ?? ''
}

const CarrierQuoteAnswerSummary = ({
  quoteAnswer,
  quoteReference,
}: CarrierQuoteAnswerSummaryProps) => {
  const { t } = useTranslation()

  return (
    <Stack direction="column" spacing={2} alignItems="center" sx={{ padding: 2 }}>
      <Paper sx={{ padding: 2, width: '60%', border: 'solid 1px', borderColor: 'primary.light' }}>
        <Stack direction="column" spacing={2} mb={2}>
          {quoteAnswer.creationDate && (
            <Typography variant="p3" component="p" color="primary.main" textAlign="right">
              {quoteAnswer.status === QuoteStatus.SUBMITTED
                ? t('screens.cs.carrier_quotes.quote_offer.accept_screen.quote_submitted_date', { date: localDate(quoteAnswer.creationDate, 'short_with_time') })
                : t('screens.cs.carrier_quotes.quote_offer.accept_screen.quote_rejected_date', { date: localDate(quoteAnswer.creationDate, 'short_with_time') })
              }
            </Typography>
          )}
          {quoteReference && (
            <Typography variant="p4" component="p" color="primary.main">
              {t('screens.cs.carrier_quotes.quote_offer.accept_screen.kn_reference', { reference: quoteReference })}
            </Typography>
          )}
        </Stack>

        {quoteAnswer.contact && (
          <Stack direction="column" spacing={2} mb={2}>
            <Typography variant="p4" color="primary.main">
              {t('screens.cs.carrier_quotes.quote_offer.accept_screen.contact_info')}
            </Typography>
            {quoteAnswer.contact.name && (
              <QuoteAnswerAttribute
                heading={t('screens.cs.carrier_quotes.quote_offer.name')}
                value={quoteAnswer.contact.name}
              />
            )}
            {quoteAnswer.contact.email && (
              <QuoteAnswerAttribute
                heading={t('screens.cs.carrier_quotes.quote_offer.email')}
                value={quoteAnswer.contact.email}
              />
            )}
          </Stack>
        )}

        <Stack direction="column" spacing={2} mb={2}>
          {isQuoteOfferAnswer(quoteAnswer) && (
            <>
              <Typography variant="p4" color="primary.main">
                {t('screens.cs.carrier_quotes.quote_offer.quote')}
              </Typography>
              <QuoteAnswerAttribute
                heading={t('screens.cs.carrier_quotes.quote_offer.my_quote')}
                value={formatDecimals(quoteAnswer.amount, { currency: quoteAnswer.currency })}
              />
              {quoteAnswer.offerReference && (
                <QuoteAnswerAttribute
                  heading={t('screens.cs.carrier_quotes.quote_offer.my_reference')}
                  value={quoteAnswer.offerReference}
                />
              )}
              {quoteAnswer.expirationDate && (
                <QuoteAnswerAttribute
                  heading={t('screens.cs.carrier_quotes.quote_offer.accept_screen.my_expiration')}
                  value={localDate(quoteAnswer.expirationDate, 'short_with_time') ?? '-'}
                />
              )}
              <QuoteAnswerAttribute
                heading={t('screens.cs.carrier_quotes.quote_offer.transit_duration')}
                value={findDayValue(quoteAnswer.transitDuration)}
              />
            </>
          )}
          {quoteAnswer.comments && (
            <QuoteAnswerAttribute
              heading={t('screens.cs.carrier_quotes.quote_offer.comments')}
              value={quoteAnswer.comments}
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}

export default CarrierQuoteAnswerSummary

